import { RadioGroup } from '@headlessui/react'
import { useEffect, useState } from 'react'
import { usePlace } from 'store/usePlace'
import { useServices } from 'store/useServices'
import { useWizardForm } from 'store/useWizardForm'
import FormattedMessage from 'utils/FormattedMessage'
import { TitleStep } from '../ui'
import { SelectablePaper } from './SelectablePaper'
import { ServiceDisableWarning } from './ServiceDisableWarning'

export const CategoryStep = () => {
  const { wizardForm, updateWizardForm } = useWizardForm()
  const { services } = useServices()
  const { place } = usePlace()
  const [showDisabledPopup, setShowDisabledPopup] = useState<boolean>(false)

  const categoryIdList = services.map(service => service.categoryId)
  const serviceWithCategory = place?.serviceCategories?.filter(category =>
    categoryIdList.includes(category.id)
  )

  const selectCategory = (categoryId: string) => {
    updateWizardForm({
      serviceCategoryId: categoryId,
      serviceId: undefined,
      serviceName: undefined,
      queueId: undefined,
      scheduleId: undefined,
    })
  }

  const selectService = (serviceId: string, serviceName: string) => {
    updateWizardForm({
      serviceCategoryId: undefined,
      serviceId,
      serviceName: serviceName,
      queueId: undefined,
      scheduleId: undefined,
    })
  }

  // if there is only one service, select it
  useEffect(() => {
    if (services.length === 1) {
      updateWizardForm({
        serviceCategoryId: undefined,
        serviceId: services[0].id,
        serviceName: services[0].name,
      })
    }
  }, [services])

  // if there is only one service, and we know the rest of the steps, go to next step
  // useEffect(() => {
  //   if (services.length === 1 && wizardForm.serviceId && !isLoading) {
  //     goToNextStep()
  //   }
  // }, [services, wizardForm.serviceId, isLoading])

  const existingCategories = place?.serviceCategories.map(c => c.id) || []
  const serviceWithoutCategory = services.filter(
    service =>
      !service.categoryId ||
      service.categoryId === 'uncategorized' ||
      !existingCategories?.includes(service.categoryId)
  )

  return (
    <>
      <ServiceDisableWarning
        propShow={showDisabledPopup}
        onClose={() => setShowDisabledPopup(false)}
      />
      <div>
        <TitleStep
          title={<FormattedMessage id='wizardForm.service.title' />}
          description={<FormattedMessage id='wizardForm.service.description' />}
        />
        <RadioGroup
          value={wizardForm.serviceId || wizardForm.serviceCategoryId}
          onChange={(value: string) => {
            const isCategory = serviceWithCategory?.some(c => c.id === value)
            if (isCategory) {
              selectCategory(value)
              return
            }
            const service = serviceWithoutCategory.find(s => s.id === value)
            if (service) selectService(service.id, service.name)
          }}
          className='mb-40 lg:mb-4'
        >
          {serviceWithCategory && serviceWithCategory.length > 0 && (
            <div className='space-y-2 md:space-y-0 md:grid md:grid-cols-2 md:gap-4'>
              {serviceWithCategory.map(category => (
                <SelectablePaper
                  key={category.id}
                  id={category.id}
                  value={category.id}
                  name={category.name}
                  showChevron
                />
              ))}
            </div>
          )}
          {serviceWithCategory && serviceWithCategory.length > 0 && serviceWithoutCategory.length > 0 && (
            <div className='my-4 border-t border-gray-200 dark:border-gray-700' />
          )}
          {serviceWithoutCategory.length > 0 && (
            <div className='space-y-2 md:space-y-0 md:grid md:grid-cols-2 md:gap-4'>
              {serviceWithoutCategory
                .sort((a, b) => {
                  if (a.position && b.position) {
                    return a.position - b.position
                  } else return 0
                })
                .map(service => {
                  const isDisabled = !service.scheduleId && !service.queueId
                  return (
                    <SelectablePaper
                      key={service.id}
                      id={service.id}
                      name={service.name}
                      value={service.id}
                      description={service.description}
                      disabled={isDisabled}
                      onClick={isDisabled ? () => setShowDisabledPopup(true) : undefined}
                    />
                  )
                })}
            </div>
          )}
        </RadioGroup>
        {serviceWithoutCategory?.length === 0 && serviceWithCategory?.length === 0 && (
          <p>
            <FormattedMessage id='wizardForm.service.empty' />
          </p>
        )}
      </div>
    </>
  )
}
